import React, { useCallback, useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
var relativeTime = require('dayjs/plugin/relativeTime')
dayjs.extend(relativeTime)
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { Formik, Field } from 'formik'
import * as UpChunk from '@mux/upchunk'
import { colors, getDisplaySize } from '../ui/helpers.js'
import { H1, H2, H3, H6, Text, Span, GradientFont, Error, SmallTitle } from '../ui/Typography.js'
import { Flex, Box, Container } from '../ui/Layout.js'
import Image from '../ui/Image.js'
import { Input, TextArea } from '../ui/Input.js'
import Icon from '../ui/Icon.js'
import CodeCopy, { copyOnClick } from '../ui/CodeCopy.js'
import Button from '../ui/Button.js'
import { BigDarkSpinner } from '../ui/Spinner.js'
import Toggle from '../ui/Toggle.js'
import Select from '../ui/Select.js'
import ColorPicker from '../ui/ColorPicker.js'
import formikFormWrapper from '../formikFormWrapper.js'
import { toast } from 'react-toastify'
import { useModal } from '../Modal.js'
import ModalContainer from '../ui/ModalContainer.js'
import { useAuth } from '../Auth.js'
import api from '../api.js'

const FormInput = formikFormWrapper(Input)

const ImageBox = styled(Flex)`
  height: 80px;
  width: 200px;
  max-width: 200px;
  border: 1px dashed #a6b7d4;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
`

const HiddenInput = styled.input`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`

const getHeightAndWidthFromDataUrl = (dataURL) =>
  new Promise((resolve) => {
    const img = new window.Image()
    img.onload = () => {
      resolve({
        height: img.height,
        width: img.width
      })
    }
    img.src = dataURL
  })

const PreviewImage = styled(Image)`
  width: 600px;
  height: 300px;
  object-fit: contain;
`

const AddLogoModal = ({ showAddLogoModal, setShowAddLogoModal }) => {
  if (!showAddLogoModal) {
    return null
  }

  const { user, setUser } = useAuth()
  const fileInputRef = useRef()
  const [previewUrl, setPreviewUrl] = useState(null)
  const [uploadedUrl, setUploadedUrl] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Show preview immediately
    const localPreview = window.URL.createObjectURL(file)
    setPreviewUrl(localPreview)
    setIsUploading(true)

    try {
      const { height, width } = await getHeightAndWidthFromDataUrl(localPreview)

      let formData = new FormData()
      formData.append('file', file)
      formData.append('name', file.name)
      formData.append('path', 'logos')
      formData.append('height', height)
      formData.append('width', width)

      const res = await api.post('/images/generic-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (res.data.payload && res.data.payload.url) {
        setUploadedUrl(res.data.payload.url)
        toast('Image uploaded')
      } else {
        toast('Error uploading image')
        setPreviewUrl(null)
      }
    } catch (err) {
      console.error(err)
      toast('Error uploading image')
      setPreviewUrl(null)
    } finally {
      setIsUploading(false)
    }
  }

  const saveImageToUser = async (logoUrl) => {
    const res = await api.post('/auth/organisation/update', { logoUrl })
    if (res.data.success) {
      const authRes = await api.get('/auth')
      setUser(authRes.data.payload)
      toast('Logo saved')
      setShowAddLogoModal(false)
    } else {
      toast('Error updating user')
    }
  }

  const displayImage = previewUrl || user.logoUrl

  return (
    <ModalContainer interiorClose width="990px" height="550px" isOpen={true} setIsOpen={setShowAddLogoModal} p="0px">
      <Flex height="100%" justifyContent="space-between">
        <Flex flexDirection="column" maxWidth="211px" borderRight="1px solid #EDF2F7;" pr="16px" p="24px">
          <H3 fontSize="20px" lineHeight="32px">
            Brand your video player
          </H3>
          <Text light mb="16px">
            Add your branding. Try to use an image with a transparent background behind your image.
          </Text>
        </Flex>
        <Flex flexDirection="column" justifyContent="center" alignItems="center" p="24px">
          <Flex flexDirection="column" justifyContent="center" alignItems="center" height="100%">
            {!displayImage ? (
              <ImageBox>
                <Icon width={16} height={16} icon="plus-grey" />
                <HiddenInput type="file" accept="image/jpg, image/png, image/jpeg, image/webp" onChange={handleFileChange} />
              </ImageBox>
            ) : (
              <>
                <PreviewImage src={displayImage} alt="Preview" />
              </>
            )}
          </Flex>
          <Flex justifyContent="flex-end" height="auto">
            <Flex>
              <Button
                ml="40px"
                variant="white"
                shadow
                onClick={() => {
                  setPreviewUrl(null)
                  setUploadedUrl(null)
                  saveImageToUser('')
                }}
                label="Remove"
              />
            </Flex>
            <Button
              mr="16px"
              variant="grey"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              label="Change"
            />
            <Button
              variant="black"
              label="Save"
              disabled={isUploading || (previewUrl && !uploadedUrl)}
              isLoading={isUploading}
              onClick={() => saveImageToUser(uploadedUrl || user.logoUrl)}
            />
          </Flex>
        </Flex>
        {/* Hidden file input for Change button */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/jpg, image/png, image/jpeg, image/webp"
          onChange={handleFileChange}
        />
      </Flex>
    </ModalContainer>
  )
}

export default AddLogoModal
